// ═══════════════════════════════════════════════════════════════
// src/index.js — React entry point + PWA service worker registration
//
// S1.A.3 (May 15, 2026) — Closes May 15 SW caching blocker.
//
// CHANGES vs prior version:
//   (1) Register SW with { updateViaCache: 'none' } — browsers were
//       caching the service-worker.js file itself, defeating the
//       version-bump strategy. With this option, the browser ALWAYS
//       refetches service-worker.js on every page load.
//   (2) Listen for 'controllerchange' on navigator.serviceWorker. When
//       the new v2 SW activates and takes control, force a one-time
//       page reload so the user gets fresh HTML + assets. Guarded so
//       it only fires once.
//   (3) Removed the listener from `load`-window-event-only pattern —
//       still registers on load, but updateViaCache:none is set inside
//       the register call where it actually applies.
//
// This pairs with service-worker.js v2 + activate-event cache nuke to
// give existing users on v1 SW a seamless upgrade: open page → browser
// fetches new service-worker.js (uncached per updateViaCache:none) →
// v2 SW installs + skipWaiting + activates → controllerchange fires →
// page reloads → user is on clean v2 with fresh HTML.
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// ── PWA service worker registration ────────────────────────────────────
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { updateViaCache: 'none' })
      .then((registration) => {
        console.log('[SW] registered:', registration.scope);
      })
      .catch((err) => {
        console.warn('[SW] registration failed:', err);
      });

    // When a new SW takes control (after skipWaiting + clients.claim),
    // reload once so the page picks up fresh HTML/assets.
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      console.log('[SW] controller changed — reloading for fresh assets');
      window.location.reload();
    });
  });
}